import {
    teamsDarkTheme,
    teamsHighContrastTheme,
    teamsLightTheme,
    Theme,
} from '@fluentui/react-components';
import { app } from '@microsoft/teams-js';
import { useCallback, useEffect, useState } from 'react';

interface TeamsContextState {
    loading: boolean;
    theme: Theme | undefined;
    themeString: string;
    isInitialized: boolean;
}

function getTheme(themeString: string): Theme {
    switch (themeString) {
        case 'dark':
            return teamsDarkTheme;
        case 'contrast':
            return teamsHighContrastTheme;
        default:
            return {
                ...teamsLightTheme,
                colorNeutralBackground3: '#eeeeee',
            };
    }
}

export function useTeamsContext(): TeamsContextState {
    const [state, setState] = useState<TeamsContextState>({
        loading: true,
        theme: undefined,
        themeString: '',
        isInitialized: false,
    });

    const handleThemeChange = useCallback((theme: string) => {
        setState((prev) => ({
            ...prev,
            themeString: theme,
            theme: getTheme(theme),
        }));
    }, []);

    useEffect(() => {
        const initializeTeams = async () => {
            try {
                await app.initialize();

                // Get initial context
                const context = await app.getContext();
                const initialTheme = context.app.theme || 'default';

                setState({
                    loading: false,
                    themeString: initialTheme,
                    theme: getTheme(initialTheme),
                    isInitialized: true,
                });

                // Register theme change handler
                app.registerOnThemeChangeHandler(handleThemeChange);

                // Notify Teams that the app loaded successfully
                app.notifySuccess();
            } catch (error) {
                console.error('Failed to initialize Teams SDK:', error);
                // Still set loading to false so the app renders
                setState((prev) => ({
                    ...prev,
                    loading: false,
                    themeString: 'default',
                    theme: getTheme('default'),
                    isInitialized: false,
                }));
            }
        };

        initializeTeams();
    }, [handleThemeChange]);

    return state;
}
